import { CampaignState, ListCampaignResponseItem } from '@/api';
import { MS_PER_HOUR, parseDateMs } from '@/utils';

const CAMPAIGN_TYPE_LABEL: Record<string, string> = {
  INSTANT_OPT_IN: 'Instant Opt-in',
  COUPON: 'Coupon',
  TRYIT: 'Try It',
};

export function deriveCampaignCardState(campaign: ListCampaignResponseItem, now: number) {
  const state = campaign.state ?? '';
  const hasClaimed = (campaign.userClaimedProductsCount ?? 0) > 0;

  const isAvailable = state === CampaignState.Available;
  const isParticipating = state === CampaignState.Participating;
  const isCompleted = state === CampaignState.Completed;

  const needsToSelectOrClaim = isParticipating && !hasClaimed;
  const isClaimedAndParticipating = isParticipating && hasClaimed;
  const isUnclaimedInvitation = isAvailable || (isParticipating && !hasClaimed);

  const invitationExpiryMs = campaign.invitationExpiresAt
    ? parseDateMs(campaign.invitationExpiresAt)
    : null;
  const isInvitationExpired = invitationExpiryMs !== null && invitationExpiryMs <= now;
  const hoursLeft =
    invitationExpiryMs !== null && !isInvitationExpired
      ? Math.ceil((invitationExpiryMs - now) / MS_PER_HOUR)
      : null;
  const isInviteExpiredUnclaimed = isUnclaimedInvitation && isInvitationExpired;

  const badgeCount = isUnclaimedInvitation && hoursLeft !== null ? hoursLeft : undefined;
  const badgeLabel = isCompleted
    ? 'Completed'
    : isInviteExpiredUnclaimed
      ? 'Expired'
      : badgeCount !== undefined
        ? `${hoursLeft === 1 ? 'hr' : 'hrs'} left`
        : undefined;
  const badgeVariant: 'success' | 'warning' = isCompleted ? 'success' : 'warning';

  const ctaLabel = isCompleted
    ? 'Campaign Completed'
    : isClaimedAndParticipating
      ? 'Post Content'
      : isInviteExpiredUnclaimed
        ? 'Invitation Expired'
        : 'View Campaign';
  const ctaVariant: 'primary' | 'secondary' =
    isCompleted || isClaimedAndParticipating ? 'secondary' : 'primary';
  const ctaDisabled = isInviteExpiredUnclaimed;

  const imageUrl = campaign.imageUrl ?? '/assets/images/placeholder.svg';
  const imageAlt: string | undefined = campaign.imageUrl ? (campaign.name ?? undefined) : '';
  const tags = ['We Ship', ...(campaign.hasSocialTasks ? ['Social Post'] : [])];
  const brandLabel = CAMPAIGN_TYPE_LABEL[campaign.campaignType ?? ''] ?? '';

  return {
    isAvailable,
    needsToSelectOrClaim,
    isCompleted,
    isClaimedAndParticipating,
    brandLabel,
    productName: campaign.name ?? '',
    imageUrl,
    imageAlt,
    tags,
    badgeCount,
    badgeLabel,
    badgeVariant,
    ctaLabel,
    ctaVariant,
    ctaDisabled,
  };
}
